import { useContext, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useTitle } from 'react-use';

import { NOTIFICATION_TYPES } from '@savgroup-front-common/constants';
import { Banner } from '@savgroup-front-common/core/atoms/Banners';
import { Heading } from '@savgroup-front-common/core/atoms/Heading';
import { Modal } from '@savgroup-front-common/core/atoms/modal';
import { NeedHelp } from '@savgroup-front-common/core/components/NeedHelp';
import {
  safeFormattedIntlString,
  SafeFormattedMessageWithoutSpread,
} from '@savgroup-front-common/core/formatters';
import { AppErrorBoundary } from '@savgroup-front-common/core/molecules/ErrorBoundary';
import { AuthConfiguration } from '@/configuration';

import { RedirectWithoutNewDesign } from '../../components/RedirectWithoutNewDesign';
import FileDetailPage from '../../newDesignPages/FileFollowupsPage/FileFollowupsPage';
import HomePage from '../../newDesignPages/HomePage/HomePage';
import { IrshPages } from '../../newDesignPages/irshPages/IrshPages';
import { OrdersPage } from '../../newDesignPages/OrdersPage';
import useInitApp from '../App.hooks';
import DebugLayout from '../DebugLayout';
import { FINAL_ROUTES } from '../routes/Routes';

import { ClientVisioPage } from './ClassiqueLayout.config';
import Header from './header/Header';
import { InitContext } from './InitProvider/InitProvider.context';
import messages from './messages';
import { $PageWrapper } from './NewLayout.styles';

interface ClassiqueLayoutProps {
  sellerId: string;
}

export default function ClassiqueLayout({ sellerId }: ClassiqueLayoutProps) {
  const { enableServicePortal } = useContext(InitContext);
  const { isAllowNewFiles } = useInitApp();
  const [isOpen, setIsOpen] = useState(isAllowNewFiles === false);

  useTitle(safeFormattedIntlString(messages.selfcareTitle));

  return (
    <AppErrorBoundary>
      <$PageWrapper>
        <NeedHelp clientId={AuthConfiguration.clientId} />
        <Header sellerId={sellerId} enableServicePortal={enableServicePortal} />

        <Modal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          shouldCloseOnDimmerClick={false}
        >
          <Heading level={3}>
            <SafeFormattedMessageWithoutSpread
              message={messages.youCannotCrateNewFileTitle}
            />
          </Heading>

          <Banner notificationType={NOTIFICATION_TYPES.ERROR} isFluid>
            <SafeFormattedMessageWithoutSpread
              message={messages.youCannotCrateNewFile}
            />
          </Banner>
        </Modal>

        <Routes>
          <Route element={<DebugLayout />}>
            <Route
              path={'/new_design/*'}
              element={<RedirectWithoutNewDesign />}
            />

            <Route path="/*" element={<IrshPages />} />

            <Route
              path={FINAL_ROUTES.VIDEO_CALL}
              element={<ClientVisioPage />}
            />
            <Route
              path={FINAL_ROUTES.FILE_FOLLOWUPS}
              element={<FileDetailPage />}
            />

            {!enableServicePortal && (
              <>
                <Route index element={<OrdersPage />} />
                <Route path={FINAL_ROUTES.PRODUCTS} element={<OrdersPage />} />
                <Route
                  path={FINAL_ROUTES.ORDER_CREATE_CLAIM}
                  element={<OrdersPage />}
                />

                <Route
                  path="/*"
                  element={<Navigate to={FINAL_ROUTES.HOME} />}
                />
              </>
            )}

            {enableServicePortal && (
              <>
                <Route
                  path={`${FINAL_ROUTES.PORTAL_SERVICE_HOME}/*`}
                  element={<HomePage />}
                />

                <Route
                  index
                  element={<Navigate to={FINAL_ROUTES.PORTAL_SERVICE_HOME} />}
                />
              </>
            )}
          </Route>
        </Routes>
      </$PageWrapper>
    </AppErrorBoundary>
  );
}
